import { Fragment, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import Magnifier from 'react-magnifier';
import cx from 'classnames';
import { SEE_ALL } from '../../constants/projectConstants';
import {
  ADMIN_MY_PRODUCTS_3DMODAL,
  ADMIN_MY_PRODUCTS_3DMODAL_EACH_V2,
  REQUEST_DETAIL_ROUTE,
  TEMPLATES_ROUTE_V2,
} from '../../constants/routePathConstants';
import { apiGetRooms } from '../../helpers/api';
import { getProjectTypeLabel } from '../../helpers/dataHelper';
import { getCuratorRoomPath, getParamsFromFilterBy } from '../../helpers/urlHelper';
import { useApiList } from '../../hooks/useApiList';
import { useRoomTypeOptions } from '../../hooks/useRoomTypes';
import {
  propDetailsPopupActions,
  selectPropDetailsPopupData,
} from '../../redux/slicers/propDetailsPopupSlicer';
import Image from '../Image';
import RightSidebar from '../RightSidebar';
import styles from './PropDetailsPopup.module.scss';
import { RiImageLine } from 'react-icons/ri';
import { setTemporaryPreventBackupPopup } from '../../redux/slicers/admin/curatorSlicer';
import { addUpdateParamsInSequence } from '../../redux/slicers/filters/filterSlicer';
import { REQUEST_STATUSES } from '../../constants/constants';
import { useUserContext } from '../../contexts/UserContext';
import { getThumbnailCardTagList } from '../../helpers/jsHelper';
import { useStaticPermissions } from '../../hooks/usePermissions';
import { PERMISSION } from '../../constants/permissionConstants';

export const PropDetailsPopup = (props) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const editItem = useSelector(selectPropDetailsPopupData);
  const { roomTypeOptions } = useRoomTypeOptions();
  const { currentUser, isGuestLogin, isGuestUser } = useUserContext();
  const { hasPermission } = useStaticPermissions();

  const onClose = () => {
    dispatch(propDetailsPopupActions.close());
  };

  const capName = editItem?.name
    ? editItem?.name.slice(0, 1).toUpperCase() + editItem?.name.slice(1).toLowerCase()
    : '';

  const requestId = editItem?.request_details?.id;
  const requestStatus = editItem?.request_details?.status;
  const isRequestOrgAndCurrentOrgSame =
    editItem?.request_details?.organization === currentUser?.organization;

  const isProductRequestPending = [
    REQUEST_STATUSES.FEEDBACK.value,
    REQUEST_STATUSES.FOR_APPROVAL.value,
  ].includes(requestStatus);

  const handleRedirectMyProduct = () => {
    if (props?.redirect && editItem?.id) {
      window.$restrictFilter = true;
      window.$scrollPosition = window.scrollY;
      window.$previousURL = `${window.location.pathname}${window.location.search}`;
      if (isProductRequestPending && isRequestOrgAndCurrentOrgSame)
        navigate(REQUEST_DETAIL_ROUTE.replace(':id', requestId));
      else {
        const canAccessV2Page = isGuestUser() || hasPermission(PERMISSION.PRODUCT_PAGE_V2);
        const isV2 = () =>
          canAccessV2Page &&
          (!editItem?.product_page_version || editItem?.product_page_version === '2');
        const url = isV2()
          ? ADMIN_MY_PRODUCTS_3DMODAL_EACH_V2?.replace(':id', editItem?.id)
          : `${ADMIN_MY_PRODUCTS_3DMODAL}/${editItem?.id}`;
        navigate(url);
      }

      dispatch(propDetailsPopupActions.close());
    }
  };

  const _tagList = useMemo(() => getThumbnailCardTagList(editItem), [editItem]);

  return (
    <RightSidebar
      backdropClassName="hideBackdrop"
      className="rightSidebar leaveHeader props"
      initialShowOpen={Boolean(editItem)}
      {...(isGuestLogin
        ? {}
        : {
            showRedirectIcon: props?.redirect && !isProductRequestPending ? true : false,
            redirectProductUI: handleRedirectMyProduct,
          })}
      onClose={onClose}
      title={
        <>
          <div className="heading-user-title">
            <h4 className="m-0 ellipsis">{capName}</h4>
            <label className="fullWidth ellipsis light">
              {editItem?.sku_id && (
                <>
                  Sku ID - <span>{editItem?.sku_id}</span>
                </>
              )}
            </label>
          </div>
        </>
      }
      body={
        <>
          {editItem?.thumbnail ? (
            <Magnifier src={editItem?.thumbnail} className={`${styles.imgPreviewContainer} mb-4`} />
          ) : (
            <div
              className={`${styles.imgPreviewContainer} d-flex justify-content-center align-items-center mb-4`}
              style={{ background: '#e9e9e9' }}>
              {_tagList.length > 0 ? (
                <div className={styles.tagList}>
                  {_tagList.map((a, i) => (
                    <div
                      key={i}
                      className={cx(styles.tag, a?.onClick ? 'cursor-pointer' : '')}
                      onClick={() => a?.onClick()}>
                      {a?.label}
                    </div>
                  ))}
                </div>
              ) : null}
              <RiImageLine fontSize={24} className={'placeholderIcon'} />
            </div>
          )}
          {roomTypeOptions.map((option) => {
            return <RoomTypeRow key={option.value} roomType={option.value} propId={editItem?.id} />;
          })}
        </>
      }
    />
  );
};

const RoomTypeRow = ({ roomType, propId }) => {
  const dispatch = useDispatch();
  const params = useMemo(() => ({ room_type: roomType }), []);

  const { list: roomList } = useApiList({
    api: apiGetRooms,
    params,
    defaultPerPage: 3,
  });

  const roomLabel = getProjectTypeLabel(roomType);

  if (!roomList.length) return null;

  const handleRoomClick = (roomId) => () => {
    dispatch(setTemporaryPreventBackupPopup({
      type: 'room',
      id: roomId,
    }));
  }

  return (
    <Fragment>
      <div className="d-flex justify-content-between align-items-end">
        <h6 className={styles.roomSectionTitle}>Create {roomLabel}</h6>
        <Link
          to={`${TEMPLATES_ROUTE_V2}?roomType=${roomType}&curatorProp=${propId}&filterOrder=filterBy&filterBy=${roomType}`}
          className={cx('nav-link', styles.roomSectionLink)}
          onClick={() => {
            dispatch(addUpdateParamsInSequence({'filterBy': getParamsFromFilterBy(roomType, 'room_type')}))
          }}
          >
          {SEE_ALL}
        </Link>
      </div>
      <div className={styles.imageList}>
        {roomList.map((room) => (
          <a
            href={getCuratorRoomPath(room.id, { propId })}
            key={room.id}
            className={styles.imageContainer}
            target="_blank"
            rel="noreferrer"
            onClick={handleRoomClick(room.id)}>
            <Image ratio={1} src={room.thumbnail} rounded placeholderWhileLoading Icon={null} />
          </a>
        ))}
      </div>
    </Fragment>
  );
};
